<template>
  <div class="ts-view">
    <div class="ts-view__metrics">
      <button
        v-for="m in GENERAL_METRICS"
        :key="m.key"
        type="button"
        class="ts-view__metric-btn"
        :class="{ active: selectedMetric === m.key }"
        @click="selectedMetric = m.key"
      >
        {{ m.label }}
      </button>
    </div>

    <div class="ts-view__costs-block">
      <span class="ts-view__costs-title">KOSTEN</span>
      <div class="ts-view__metrics ts-view__metrics--costs ts-view__metrics--costs-grouped">
        <button
          type="button"
          class="ts-view__metric-btn"
          :class="{ active: selectedMetric === COST_METRICS[0].key }"
          @click="selectedMetric = COST_METRICS[0].key"
        >
          {{ COST_METRICS[0].label }}
        </button>

        <span class="ts-view__costs-divider" aria-hidden="true" />

        <button
          v-for="m in COST_METRICS.slice(1)"
          :key="m.key"
          type="button"
          class="ts-view__metric-btn"
          :class="{ active: selectedMetric === m.key }"
          @click="selectedMetric = m.key"
        >
          {{ m.label }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="ts-view__state">Lade Verlaufsdaten…</div>
    <div v-else-if="error" class="ts-view__state ts-view__state--error">{{ error }}</div>
    <template v-else-if="data">
      <div class="ts-view__chart-panel">
        <TimeseriesChart
          :months="data.months"
          :vorjahr="data.vorjahr"
          :metric="selectedMetric"
          :effective-month="data.effective_month"
          :year="year"
        />
      </div>
      <div class="ts-view__monthly-panel">
        <TimeseriesTable
          :months="data.months"
          :vorjahr="data.vorjahr"
          :metric="selectedMetric"
          :effective-month="data.effective_month"
        />
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { getApiBaseUrl } from '@/config';
import TimeseriesChart from './TimeseriesChart.vue';
import TimeseriesTable from './TimeseriesTable.vue';

const GENERAL_METRICS = [
  { key: 'sales', label: 'Sales Erlöse' },
  { key: 'erloese', label: 'Umsatz abg.' },
  { key: 'ergebnis', label: 'Cockpit Ergebnis' },
];

const COST_METRICS = [
  { key: 'kosten', label: 'Kosten gesamt' },
  { key: 'kosten_ff_gesamt', label: 'Kosten FF gesamt' },
  { key: 'ff_studienkosten', label: 'FF Studienkosten' },
  { key: 'ff_umlage', label: 'FF Umlage' },
  { key: 'dienstleister_studienkosten', label: 'Dienstleister Studienkosten' },
  { key: 'werbekosten_gesamt', label: 'Werbekosten gesamt' },
  { key: 'personalkosten_gesamt', label: 'Personalkosten gesamt' },
];

export default {
  components: { TimeseriesChart, TimeseriesTable },
  props: {
    projektNummer: { type: String, default: null },
    year: { type: Number, required: true },
  },
  data() {
    return {
      GENERAL_METRICS,
      COST_METRICS,
      selectedMetric: 'sales',
      data: null,
      loading: false,
      error: null,
      requestId: 0,
    };
  },
  watch: {
    projektNummer: { immediate: true, handler: 'fetchData' },
    year: 'fetchData',
  },
  methods: {
    async fetchData() {
      if (!this.projektNummer) {
        this.data = null;
        this.error = null;
        this.loading = false;
        return;
      }

      const currentRequestId = ++this.requestId;
      this.loading = true;
      this.error = null;
      try {
        const apiUrl = getApiBaseUrl();
        const res = await axios.get(
          `${apiUrl}/projects/${this.projektNummer}/timeseries/`,
          { params: { year: this.year }, withCredentials: true }
        );
        if (currentRequestId !== this.requestId) {
          return;
        }
        this.data = res.data;
      } catch {
        if (currentRequestId !== this.requestId) {
          return;
        }
        this.data = null;
        this.error = 'Verlaufsdaten konnten nicht geladen werden.';
      } finally {
        if (currentRequestId === this.requestId) {
          this.loading = false;
        }
      }
    },
  },
};
</script>

<style scoped>
.ts-view {
  padding: 6px 0 0;
}

.ts-view__metrics {
  display: flex;
  gap: 8px;
  padding: 0 0 12px;
  flex-wrap: wrap;
}

.ts-view__costs-block {
  margin-bottom: 8px;
  border-left: 1px solid var(--ds-border);
  padding-left: 10px;
}

.ts-view__costs-title {
  display: block;
  margin: 0 0 6px;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  font-weight: 700;
  color: var(--ds-text-subtle);
}

.ts-view__metrics--costs {
  padding-bottom: 14px;
}

.ts-view__metrics--costs-grouped {
  align-items: center;
}

.ts-view__costs-divider {
  width: 1px;
  height: 20px;
  background: var(--ds-border);
  display: inline-block;
  margin: 0 2px;
}

.ts-view__metric-btn {
  /* ADS-Subtle-Button */
  height: 32px;
  border: none;
  border-radius: var(--ds-radius-small);
  background: var(--ds-neutral);
  color: var(--ds-text-subtle);
  font-size: 14px;
  font-weight: 500;
  padding: 0 12px;
  cursor: pointer;
  transition: all 0.15s;
}

.ts-view__metric-btn:hover {
  background: var(--ds-neutral-hovered);
}

.ts-view__metric-btn.active {
  background: var(--ds-selected-bg);
  color: var(--ds-selected-text);
}

.ts-view__chart-panel {
  margin: 0;
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-radius-large);
  background: var(--ds-surface);
}

.ts-view__state {
  padding: 24px 16px;
  color: var(--ds-text-subtle);
  font-size: 0.9rem;
}

.ts-view__state--error {
  color: var(--ds-danger-text);
}

/* Keine Karte in der Karte: die Tabelle sitzt direkt in der Verlauf-Karte. */
.ts-view__monthly-panel {
  margin: 24px 0 0;
}
</style>
